import React, { useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Text, Colors, Icon } from '@mercator-gcp/grout';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import SelectPillButton from 'components/widgets/SearchFilterWizard/steps/components/SelectPillButton';
import { useSectors } from 'state/projectTypes';

const searchContainerStyle = {
  marginBottom: 'padding_8',
};

const sectorButtonsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'padding_12',
  marginBottom: 'padding_16',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
    marginBottom: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
    marginBottom: 'padding_8',
  },
};

const checkboxContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'padding_8',
  marginTop: 'padding_8',
  maxHeight: '400px',
  overflowY: 'auto',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'padding_4',
  },
};

const checkboxItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: 'padding_4',
};

const customCheckboxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 'padding_8',
  cursor: 'pointer',
  width: '100%',
};

const checkboxBoxStyle = (isChecked: boolean) => ({
  width: '16px',
  height: '16px',
  minWidth: '16px',
  borderRadius: '4px',
  border: `1px solid ${isChecked ? Colors.Tangerine900 : Colors.ArmyGreen200}`,
  backgroundColor: isChecked ? Colors.Tangerine900 : Colors.White,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  flexShrink: 0,
});

type ExcludeSectorsStepProps = {
  excludedVerticals: number[];
  onExcludedVerticalsChange: (verticals: number[]) => void;
  selectedVerticals: number[];
};

const ExcludeSectorsStep = ({
  excludedVerticals,
  onExcludedVerticalsChange,
  selectedVerticals,
}: ExcludeSectorsStepProps) => {
  const { verticals: allVerticals, sectorsLoading, verticalsLoading } = useSectors();
  const [searchQuery, setSearchQuery] = useState('');

  const sortedVerticals = useMemo(() => {
    return [...allVerticals].sort((a, b) => a.name.localeCompare(b.name));
  }, [allVerticals]);

  // Group verticals by sector
  const verticalsBySector = useMemo(() => {
    const grouped = new Map<string, typeof allVerticals>();
    allVerticals.forEach((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';
      if (!grouped.has(sectorName)) {
        grouped.set(sectorName, []);
      }
      grouped.get(sectorName)!.push(vertical);
    });

    return grouped;
  }, [allVerticals]);

  // Filter out sectors that have any selected verticals from the previous step
  const availableSectors = useMemo(() => {
    const selectedSectorNames = new Set<string>();

    // Find all sectors that have selected verticals
    allVerticals.forEach((vertical) => {
      if (selectedVerticals.includes(vertical.id)) {
        const sectorName = vertical.sector?.name || 'Other';
        selectedSectorNames.add(sectorName);
      }
    });

    // Return only sectors that don't have any selected verticals
    return Array.from(verticalsBySector.keys())
      .filter((sectorName) => !selectedSectorNames.has(sectorName))
      .sort();
  }, [verticalsBySector, selectedVerticals, allVerticals]);

  const filteredVerticals = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedVerticals;
    }
    const query = searchQuery.toLowerCase();

    return sortedVerticals.filter((vertical) =>
      vertical.name.toLowerCase().includes(query),
    );
  }, [sortedVerticals, searchQuery]);

  // Filter out verticals that belong to selected sectors
  const availableVerticals = useMemo(() => {
    const selectedSectorNames = new Set<string>();

    // Find all sectors that have selected verticals
    allVerticals.forEach((vertical) => {
      if (selectedVerticals.includes(vertical.id)) {
        const sectorName = vertical.sector?.name || 'Other';
        selectedSectorNames.add(sectorName);
      }
    });

    // Return only verticals from sectors that weren't selected
    return filteredVerticals.filter((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';

      return !selectedSectorNames.has(sectorName);
    });
  }, [filteredVerticals, selectedVerticals, allVerticals]);

  // Get all available verticals by sector (for checking selection state regardless of search)
  const allAvailableVerticalsBySector = useMemo(() => {
    const grouped = new Map<string, typeof availableVerticals>();
    availableVerticals.forEach((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';
      if (!grouped.has(sectorName)) {
        grouped.set(sectorName, []);
      }
      grouped.get(sectorName)!.push(vertical);
    });

    return grouped;
  }, [availableVerticals]);

  // Get filtered available verticals (available verticals filtered by search query)
  const filteredAvailableVerticals = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableVerticals;
    }
    const query = searchQuery.toLowerCase();

    return availableVerticals.filter((vertical) =>
      vertical.name.toLowerCase().includes(query),
    );
  }, [availableVerticals, searchQuery]);

  // Get filtered verticals by sector (for sector buttons) - only for available sectors
  const filteredVerticalsBySector = useMemo(() => {
    const grouped = new Map<string, typeof availableVerticals>();
    filteredAvailableVerticals.forEach((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';
      if (!grouped.has(sectorName)) {
        grouped.set(sectorName, []);
      }
      grouped.get(sectorName)!.push(vertical);
    });

    return grouped;
  }, [filteredAvailableVerticals]);

  const handleVerticalToggle = (verticalId: number) => {
    const newExcluded = excludedVerticals.includes(verticalId)
      ? excludedVerticals.filter((id) => id !== verticalId)
      : [...excludedVerticals, verticalId];

    onExcludedVerticalsChange(newExcluded);
  };

  const handleSelectAllSector = (sectorName: string) => {
    const sectorVerticals = filteredVerticalsBySector.get(sectorName) || [];
    const sectorVerticalIds = sectorVerticals.map((v) => v.id);
    const allSectorExcluded = sectorVerticalIds.every((id) =>
      excludedVerticals.includes(id),
    );

    if (allSectorExcluded) {
      // Deselect all verticals in this sector
      const newExcluded = excludedVerticals.filter(
        (id) => !sectorVerticalIds.includes(id),
      );
      onExcludedVerticalsChange(newExcluded);
    } else {
      // Select all verticals in this sector (merge with existing selections)
      const newExcluded = Array.from(
        new Set([...excludedVerticals, ...sectorVerticalIds]),
      );
      onExcludedVerticalsChange(newExcluded);
    }
  };

  if (sectorsLoading || verticalsLoading) {
    return (
      <SectionWrapper>
        <Header title='Are there any "no-go" sectors for you?' description="" />
        <Text.Small>Loading sectors...</Text.Small>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper>
      <Header
        title='Are there any "no-go" sectors for you?'
        description="Select any sectors you'd like to skip. We'll filter these out so you only see relevant leads."
      />
      {sortedVerticals.length > 0 && (
        <FlexColumn sx={{ gap: 'padding_8' }}>
          {availableSectors.length === 0 && (
            <Text.Small
              sx={{ color: Colors.TextQuiet, textAlign: 'center', padding: 'padding_24' }}
            >
              All sectors have been selected. There are no sectors available to exclude.
            </Text.Small>
          )}
          {availableSectors.length > 0 && (
            <>
              {/* Sector selection buttons - always show all available categories */}
              <Box sx={sectorButtonsContainerStyle}>
                {availableSectors.map((sectorName) => {
                  // Get filtered verticals for this sector (based on search query)
                  const sectorVerticals = filteredVerticalsBySector.get(sectorName) || [];
                  // Get all available verticals for this sector (regardless of search)
                  const allSectorVerticals =
                    allAvailableVerticalsBySector.get(sectorName) || [];

                  // Check if any sectors in this category are excluded (even if not visible due to search)
                  // This ensures the button shows as selected if sectors are excluded, regardless of search filter
                  const allSectorVerticalIds = allSectorVerticals.map((v) => v.id);
                  const hasAnyExcluded = allSectorVerticalIds.some((id) =>
                    excludedVerticals.includes(id),
                  );
                  // Also check if all filtered sectors are excluded (for when search is active)
                  const sectorVerticalIds = sectorVerticals.map((v) => v.id);
                  const allFilteredExcluded =
                    sectorVerticalIds.length > 0 &&
                    sectorVerticalIds.every((id) => excludedVerticals.includes(id));
                  // Show as selected if any sectors in the category are excluded OR all filtered sectors are excluded
                  const allSectorExcluded = hasAnyExcluded || allFilteredExcluded;

                  return (
                    <SelectPillButton
                      key={sectorName}
                      isSelected={allSectorExcluded}
                      onClick={() => handleSelectAllSector(sectorName)}
                      variant="exclude"
                    >
                      All {sectorName}
                    </SelectPillButton>
                  );
                })}
              </Box>
              <Box sx={searchContainerStyle}>
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search sectors"
                  showClear
                />
              </Box>
              {filteredAvailableVerticals.length > 0 && (
                <Box sx={checkboxContainerStyle}>
                  {filteredAvailableVerticals.map((vertical) => {
                    const isChecked = excludedVerticals.includes(vertical.id);

                    return (
                      <Box key={vertical.id} sx={checkboxItemStyle}>
                        <Box
                          sx={customCheckboxStyle}
                          onClick={() => handleVerticalToggle(vertical.id)}
                          role="checkbox"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleVerticalToggle(vertical.id);
                            }
                          }}
                        >
                          <Box sx={checkboxBoxStyle(isChecked)}>
                            {isChecked && (
                              <Icon
                                name={Icon.Name.CloseX}
                                size={10}
                                fill={Colors.White}
                              />
                            )}
                          </Box>
                          <Text.Body>{vertical.name}</Text.Body>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {filteredAvailableVerticals.length === 0 && searchQuery.trim() && (
                <Text.Small
                  sx={{
                    color: Colors.TextQuiet,
                    textAlign: 'center',
                    padding: 'padding_24',
                  }}
                >
                  No industries found matching &quot;{searchQuery}&quot;
                </Text.Small>
              )}
            </>
          )}
        </FlexColumn>
      )}
    </SectionWrapper>
  );
};

export default ExcludeSectorsStep;
